import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cube, Spinner, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFB]">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 rounded-md bg-emerald-500 flex items-center justify-center"><Cube size={18} weight="bold" className="text-white" /></div>
          <span className="font-display font-bold text-lg">Orbit Inventory Management</span>
        </Link>
        <h1 className="font-display text-3xl font-bold tracking-tight">Set a new password</h1>
        <p className="text-zinc-500 mt-1 mb-8">Choose a strong password for your account.</p>
        {!token ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">Missing reset token. Please use the link from your email.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">New password</Label>
              <div className="relative mt-1.5">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-12 pr-11 focus:ring-2 focus:ring-emerald-500" data-testid="reset-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="reset-submit">
              {loading ? <Spinner size={20} className="animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
